import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormHelperText,
  Alert,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  CircularProgress,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import AccessibleIcon from "@mui/icons-material/Accessible";
import PeopleIcon from "@mui/icons-material/People";
import { getAllEvents, rsvpToEvent } from "../../apis/user-apis/EventApis";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "auto", block: "start" });
};

const NAME_REGEX = /^[\p{L}\p{M}\s'-’]+$/u;
const filterName = (value) => value.replace(/[^\p{L}\p{M}\s'-’]/gu, "");

// ── Detail + RSVP Modal ───────────────────────────────────────────────────────
const EventModal = ({ open, onClose, event, onRsvpSuccess }) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    numberOfGuests: "1",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Reset form state every time the modal opens
  useEffect(() => {
    if (open) {
      setFormData({ fullName: "", email: "", phone: "", numberOfGuests: "1" });
      setErrors({});
      setLoading(false);
      setSuccess(false);
    }
  }, [open]);

  if (!event) return null;

  const hasCapacity = event.capacity != null && event.capacity > 0;
  const spotsLeft = hasCapacity ? event.capacity - event.registered : null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "fullName") {
      nextValue = filterName(value);
    } else if (name === "phone") {
      nextValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const errs = {};
    const nameTrimmed = formData.fullName.trim();
    if (!nameTrimmed) {
      errs.fullName = "Full name is required";
    } else if (nameTrimmed.split(/\s+/).filter(Boolean).length < 2) {
      errs.fullName = "Please enter full name";
    } else if (!NAME_REGEX.test(nameTrimmed)) {
      errs.fullName = "Enter a valid name";
    }
    if (!formData.email.trim()) errs.email = "Email is required";
    else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email))
      errs.email = "Invalid email address";
    if (!formData.phone.trim()) errs.phone = "Phone is required";
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, "")))
      errs.phone = "Phone must be 10 digits";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const res = await rsvpToEvent(event._id, {
        ...formData,
        numberOfGuests: parseInt(formData.numberOfGuests),
      });

      if (res.success) {
        setSuccess(true);
        if (onRsvpSuccess) onRsvpSuccess();
        setTimeout(() => {
          setSuccess(false);
          onClose();
          setFormData({
            fullName: "",
            email: "",
            phone: "",
            numberOfGuests: "1",
          });
        }, 3000);
      } else {
        setErrors({ submit: res.error || "Failed to RSVP" });
      }
    } catch (err) {
      setErrors({
        submit:
          err?.response?.data?.error ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { borderRadius: "8px", maxHeight: "92vh" } }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          bgcolor: "#0A192F",
          color: "#fff",
          pb: 2,
          pr: 6,
          position: "relative",
        }}
      >
        <Typography
          sx={{
            color: "#C59324",
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "2px",
            textTransform: "uppercase",
            mb: 1,
          }}
        >
          {event.type}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.4rem",
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          {event.title}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.7)",
            "&:hover": { color: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Event Details */}
        <Box
          sx={{
            bgcolor: "#FDFAF4",
            p: 3,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: { xs: 2, sm: 2.5 },
          }}
        >
          <Box>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <CalendarTodayIcon sx={{ fontSize: "0.75rem" }} /> DATE
            </Typography>
            <Typography
              sx={{ color: "#051328", fontWeight: 600, fontSize: "0.9rem" }}
            >
              {event.date}
            </Typography>
          </Box>

          <Box>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <AccessTimeIcon sx={{ fontSize: "0.75rem" }} /> TIME
            </Typography>
            <Typography
              sx={{ color: "#051328", fontWeight: 600, fontSize: "0.9rem" }}
            >
              {event.time} {event.duration ? `· ${event.duration} minutes` : ""}
            </Typography>
          </Box>

          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <LocationOnIcon sx={{ fontSize: "0.75rem" }} /> LOCATION
            </Typography>
            <Typography
              sx={{ color: "#D32F2F", fontWeight: 600, fontSize: "0.9rem" }}
            >
              📍 {event.location}
            </Typography>
            <Typography sx={{ color: "#5D6785", fontSize: "0.82rem" }}>
              {event.address}
            </Typography>
          </Box>

          {hasCapacity && (
          <Box>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <PeopleIcon sx={{ fontSize: "0.75rem" }} /> {event.capacity > 500 ? "EXPECTED ATTENDANCE" : "AVAILABILITY"}
            </Typography>
            <Typography
              sx={{ color: "#051328", fontSize: "0.9rem", fontWeight: 600 }}
            >
              {event.capacity > 500
                ? `${event.capacity.toLocaleString()} attendees expected`
                : `${spotsLeft.toLocaleString()} spot${spotsLeft !== 1 ? "s" : ""} remaining`}
            </Typography>
          </Box>
          )}

          {event.parking && (
          <Box>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <DirectionsCarIcon sx={{ fontSize: "0.75rem" }} /> PARKING
            </Typography>
            <Typography sx={{ color: "#5D6785", fontSize: "0.82rem" }}>
              {event.parking}
            </Typography>
          </Box>
          )}

          {event.accessibility && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Typography
              sx={{
                color: "#C59324",
                fontSize: "0.65rem",
                fontWeight: 700,
                letterSpacing: "2px",
                textTransform: "uppercase",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <AccessibleIcon sx={{ fontSize: "0.75rem" }} /> ACCESSIBILITY
            </Typography>
            <Typography sx={{ color: "#5D6785", fontSize: "0.82rem" }}>
              {event.accessibility}
            </Typography>
          </Box>
          )}
        </Box>

        {/* Description */}
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #eee" }}>
          <Typography
            sx={{ color: "#374151", fontSize: "0.92rem", lineHeight: 1.7 }}
          >
            {event.description}
          </Typography>
        </Box>

        {/* RSVP Form */}
        <Box sx={{ px: 3, py: 3 }}>
          {success ? (
            <Alert severity="success" sx={{ fontSize: "0.95rem" }}>
              🎉 You're confirmed! We'll see you there.
            </Alert>
          ) : (
            <>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  color: "#051328",
                  mb: 2.5,
                }}
              >
                Come Join Us
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#051328",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    FULL NAME <span style={{ color: "#D32F2F" }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="First and last name"
                    error={!!errors.fullName}
                    sx={formFieldSx}
                  />
                  {errors.fullName && (
                    <FormHelperText error>{errors.fullName}</FormHelperText>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <Box sx={{ flex: 1, minWidth: "140px" }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#051328",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      EMAIL <span style={{ color: "#D32F2F" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      sx={formFieldSx}
                    />
                    {errors.email && (
                      <FormHelperText error>{errors.email}</FormHelperText>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: "140px" }}>
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: "#051328",
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      PHONE <span style={{ color: "#D32F2F" }}>*</span>
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(302) 555-0123"
                      error={!!errors.phone}
                      sx={formFieldSx}
                    />
                    {errors.phone && (
                      <FormHelperText error>{errors.phone}</FormHelperText>
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#051328",
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                      mb: 0.5,
                    }}
                  >
                    # ATTENDING <span style={{ color: "#D32F2F" }}>*</span>
                  </Typography>
                  <FormControl fullWidth size="small">
                    <Select
                      name="numberOfGuests"
                      value={formData.numberOfGuests}
                      onChange={handleChange}
                      sx={formFieldSx}
                    >
                      <MenuItem value="1">1 (just me)</MenuItem>
                      <MenuItem value="2">2 people</MenuItem>
                      <MenuItem value="3">3 people</MenuItem>
                      <MenuItem value="4">4 people</MenuItem>
                      <MenuItem value="5">5 people</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {errors.submit && (
                  <Alert severity="error">{errors.submit}</Alert>
                )}
              </Box>
            </>
          )}
        </Box>
      </DialogContent>

      {!success && (
        <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              bgcolor: "#C59324",
              color: "#fff",
              fontWeight: 700,
              py: 1.5,
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontSize: "0.85rem",
              borderRadius: "4px",
              "&:hover": { bgcolor: "#A67C1E" },
            }}
          >
            {loading ? "Confirming..." : "CONFIRM JOIN US"}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const formFieldSx = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#E5E7EB" },
    "&:hover fieldset": { borderColor: "#C59324" },
    "&.Mui-focused fieldset": { borderColor: "#C59324" },
  },
};

// ── Main EventList ────────────────────────────────────────────────────────────
const EventList = () => {
  const [modal, setModal] = useState({ open: false, event: null });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const data = await getAllEvents();
      if (data.success) {
        setEvents(data.events || []);
      } else {
        setError(data.error || "Failed to load events");
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleRsvpSuccess = () => {
    // Refresh events to update registration counts
    fetchEvents();
  };

  return (
    <Box id="events" sx={{ bgcolor: "#FDFBF7", py: 4, width: "100%" }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography
            sx={{
              color: "#C59324",
              fontWeight: 700,
              letterSpacing: "0.2em",
              fontSize: "0.85rem",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            <Box
              component="span"
              sx={{ width: 30, height: 1.5, bgcolor: "#C59324", mr: 1.5 }}
            />
            UPCOMING EVENTS
          </Typography>
          <Typography
            sx={{
              mb: "8px",
              color: "#333",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            Come{" "}
            <Box
              component="span"
              sx={{
                color: "#C59324",
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Meet Gary
            </Box>
          </Typography>
          <Typography
            sx={{
              color: "#5D6785",
              fontSize: "1.05rem",
              lineHeight: 1.6,
              maxWidth: "500px",
            }}
          >
            Town halls, canvassing events, and community appearances across
            Middletown. Find an event near you.
          </Typography>
        </Box>

        {/* Event Cards or Status Messages */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minHeight: "200px",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#C59324" }} />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{ color: "#D32F2F" }}>{error}</Typography>
              <Button
                onClick={fetchEvents}
                sx={{ mt: 2, color: "#C59324", fontWeight: 700 }}
              >
                Try Again
              </Button>
            </Box>
          ) : events.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 10,
                bgcolor: "#FFF",
                borderRadius: "8px",
                border: "1px dashed #E5E7EB",
              }}
            >
              <Typography
                sx={{
                  color: "#5D6785",
                  fontSize: "1.1rem",
                  fontFamily: "serif",
                }}
              >
                No upcoming events at the moment. Please check back soon!
              </Typography>
            </Box>
          ) : (
            events.map((event, index) => {
              const dateObj = new Date(event.eventDate || event.date);
              const displayMonth =
                event.month ||
                dateObj
                  .toLocaleString("en-US", { month: "short" })
                  .toUpperCase();
              const displayDay = event.day || dateObj.getDate();
              const displayType = event.type || event.eventType;
              const displayLocation = event.location || event.venueName;

              // Prepare event for modal with all fallbacks
              const modalEvent = {
                ...event,
                month: displayMonth,
                day: displayDay,
                type: displayType,
                location: displayLocation,
                date:
                  event.date ||
                  dateObj.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }),
                parking: event.parking || event.parkingInfo || "",
                accessibility:
                  event.accessibility || event.accessibilityInfo || "",
              };

              return (
                <Box
                  key={event.id || event._id || index}
                  sx={{
                    display: "flex",
                    alignItems: { xs: "flex-start", sm: "center" },
                    bgcolor: "#FFF",
                    p: { xs: 2, sm: 3 },
                    borderRadius: "6px",
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0px 4px 20px rgba(0,0,0,0.02)",
                    flexWrap: "nowrap",
                    gap: { xs: 1.5, sm: 2 },
                  }}
                >
                  {/* Date block */}
                  <Box
                    sx={{
                      bgcolor: "#051328",
                      color: "#FFF",
                      p: { xs: 1.5, sm: 2 },
                      minWidth: { xs: "52px", sm: "70px" },
                      borderRadius: "4px",
                      textAlign: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.7rem", fontWeight: 700, mb: 0.5 }}
                    >
                      {displayMonth}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "1.4rem", sm: "1.8rem" },
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      {displayDay}
                    </Typography>
                  </Box>

                  {/* Details + Button wrapper */}
                  <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: 1.5, sm: 2 } }}>
                  {/* Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: "#C59324",
                        fontSize: "0.65rem",
                        fontWeight: 700,
                        letterSpacing: "1px",
                        mb: 0.5,
                      }}
                    >
                      {displayType}
                    </Typography>
                    <Typography
                      sx={{
                        color: "#051328",
                        fontSize: { xs: "0.85rem", sm: "1.1rem" }, // ← smaller on mobile
                        fontWeight: 800,
                        mb: 1,
                        // prevent overflow
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {event.title}
                    </Typography>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <LocationOnIcon
                        sx={{
                          color: "#D32F2F",
                          fontSize: "0.9rem",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#5D6785",
                          fontSize: { xs: "0.75rem", sm: "0.9rem" },
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {displayLocation}{event.address ? `, ${event.address}` : ""}{event.city ? `, ${event.city}` : ""}{event.zipCode ? ` ${event.zipCode}` : ""}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
                    >
                      <AccessTimeIcon
                        sx={{
                          color: "#C59324",
                          fontSize: "0.9rem",
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#5D6785",
                          fontSize: { xs: "0.75rem", sm: "0.9rem" },
                        }}
                      >
                        {event.time || event.eventTime}{event.duration ? ` · ${event.duration} min` : ""}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Join Us button */}
                  <Button
                    variant="contained"
                    onClick={() => setModal({ open: true, event: modalEvent })}
                    disabled={event.capacity > 0 && event.registered >= event.capacity}
                    sx={{
                      bgcolor:
                        event.capacity > 0 && event.registered >= event.capacity
                          ? "#9CA3AF"
                          : "#C59324",
                      color: "#FFF",
                      fontWeight: 700,
                      textTransform: "none",
                      px: { xs: 2, sm: 4 },
                      py: { xs: 1, sm: 1.5 },
                      borderRadius: "4px",
                      fontSize: { xs: "0.8rem", sm: "0.9rem" },
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                      "&:hover": {
                        bgcolor:
                          event.capacity > 0 && event.registered >= event.capacity
                            ? "#9CA3AF"
                            : "#A67C1E",
                      },
                    }}
                  >
                    {event.capacity > 0 && event.registered >= event.capacity
                      ? "Sold Out"
                      : "Join Us →"}
                  </Button>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer link */}
        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Link
            href="#volunteer"
            onClick={(e) => {
              e.preventDefault();
              scrollTo("#volunteer");
            }}
            sx={{
              color: "#C59324",
              fontWeight: 700,
              fontSize: "1rem",
              textDecoration: "none",
              borderBottom: "2px solid transparent",
              transition: "0.3s",
              cursor: "pointer",
              "&:hover": { borderBottomColor: "#C59324" },
            }}
          >
            See all events & volunteer to help →
          </Link>
        </Box>
      </Container>

      <EventModal
        open={modal.open}
        onClose={() => setModal({ open: false, event: null })}
        event={modal.event}
        onRsvpSuccess={handleRsvpSuccess}
      />
    </Box>
  );
};

export default EventList;
